import type React from "react";
import { useState } from "react";
import { Search, ChevronDown, Filter, X } from "lucide-react";
import { AddPatientIcon } from "./icons/patientListIcons";
import { Link } from "react-router-dom";
import Button from "./ButtonComponent";
import { CSS_CLASSES } from "../constants/app";

interface FilterOption {
  label: string;
  value: string;
}

interface FilterComponentProps {
  searchQuery?: string;
  riskLevel?: string;
  ageGroup?: string;
  gender?: string;
  onSearch?: (query: string) => void;
  onRiskLevelChange?: (value: string) => void;
  onAgeGroupChange?: (value: string) => void;
  onGenderChange?: (value: string) => void;
  onClearFilters?: () => void;
}

export default function FilterComponent({
  searchQuery = "",
  riskLevel = "all",
  ageGroup = "all",
  gender = "all",
  onSearch,
  onRiskLevelChange,
  onAgeGroupChange,
  onGenderChange,
  onClearFilters,
}: FilterComponentProps) {
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  const riskLevelOptions: FilterOption[] = [
    { label: "All Risk Levels", value: "all" },
    { label: "High Risk", value: "poor" },
    { label: "Medium Risk", value: "average" },
    { label: "Low Risk", value: "good" },
  ];

  const ageGroupOptions: FilterOption[] = [
    { label: "All Ages", value: "all" },
    { label: "18-35", value: "18-35" },
    { label: "36-50", value: "36-50" },
    { label: "51-65", value: "51-65" },
    { label: "65+", value: "65+" },
  ];

  const genderOptions: FilterOption[] = [
    { label: "All Genders", value: "all" },
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
    { label: "Other", value: "other" },
  ];

  const selectedRiskLevel =
    riskLevel === "all"
      ? "Risk level"
      : riskLevelOptions.find((option) => option.value === riskLevel)?.label ??
        "Risk level";
  const selectedAgeGroup =
    ageGroup === "all"
      ? "Age group"
      : ageGroupOptions.find((option) => option.value === ageGroup)?.label ??
        "Age group";
  const selectedGender =
    gender === "all"
      ? "Gender"
      : genderOptions.find((option) => option.value === gender)?.label ??
        "Gender";

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSearch?.(e.target.value);
  };

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  // Check if any filter is active
  const hasActiveFilters = () => {
    return (
      searchQuery !== "" ||
      riskLevel !== "all" ||
      ageGroup !== "all" ||
      gender !== "all"
    );
  };

  // Clear all filters
  const handleClearFilters = () => {
    setActiveDropdown(null);
    setIsMobileFilterOpen(false);

    if (onClearFilters) {
      onClearFilters();
      return;
    }

    // Call all callbacks with default values
    onSearch?.("");
    onRiskLevelChange?.("all");
    onAgeGroupChange?.("all");
    onGenderChange?.("all");
  };

  const handleOptionSelect = (
    _dropdown: string,
    option: FilterOption,
    callback?: (value: string) => void
  ) => {
    callback?.(option.value);
    setActiveDropdown(null);
  };

  const pillButtonClass =
    "flex w-full items-center gap-2 !h-10 !rounded-full !px-3 !py-0 !text-sm !font-normal !shadow-none hover:!bg-guardian-20 whitespace-nowrap";

  const addPatientClass =
    "inline-flex w-full md:w-[110px] md:shrink-0 h-8 items-center justify-center gap-1.5 rounded-full bg-rag-green hover:bg-[#0B7A72] px-2.5 text-xs font-semibold text-white transition focus:outline-none focus:ring-2 focus:ring-rag-green/40";

  const renderDropdown = (
    id: string,
    selectedValue: string,
    options: FilterOption[],
    callback?: (value: string) => void
  ) => {
    return (
      <div
        className={`relative w-full md:w-auto ${
          activeDropdown === id ? "z-[60]" : "z-10"
        }`}
      >
        <Button
          onClick={() => handleDropdownToggle(id)}
          className={`${pillButtonClass} justify-between md:!w-auto`}
        >
          <span>{selectedValue}</span>
          <ChevronDown className="h-5 w-5 text-guardian-80" />
        </Button>

        {activeDropdown === id && (
          <div
            className={`${CSS_CLASSES.DROPDOWN_MENU.PANEL} ${CSS_CLASSES.DROPDOWN_MENU.PANEL_LEFT}`}
          >
            {options.map((option) => (
              <button
                key={option.value}
                onClick={() =>
                  handleOptionSelect(id, option, callback)
                }
                className={`${CSS_CLASSES.DROPDOWN_MENU.ITEM} ${
                  option.label === selectedValue
                    ? CSS_CLASSES.DROPDOWN_MENU.ITEM_SELECTED
                    : ""
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="relative z-20 bg-white">
      {activeDropdown && (
        <div
          className="fixed inset-0 z-30 hidden md:block"
          onClick={() => setActiveDropdown(null)}
        />
      )}
      {isMobileFilterOpen && !activeDropdown && (
        <div
          className="fixed inset-0 z-30 md:hidden"
          onClick={() => setIsMobileFilterOpen(false)}
        />
      )}
      <div className="relative z-40 min-w-0">
        <div className="flex flex-col gap-2 px-4 py-2.5 md:h-[60px] md:flex-row md:items-center md:justify-between">
          {/* Search Input */}
          <div className="hidden w-full min-w-0 md:block md:w-auto md:shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-guardian-80" />
              <input
                type="text"
                placeholder="Search patients"
                value={searchQuery}
                onChange={handleSearchChange}
                className="h-10 w-full md:w-auto pl-10 pr-3 rounded-full bg-guardian-10 focus:outline-none text-sm font-normal text-gray-900 placeholder:text-gray-900"
              />
            </div>
          </div>

          {/* Desktop Filter Buttons - Hidden on mobile */}
          <div className="hidden md:flex md:flex-1 items-center justify-end gap-2 min-w-0">
            {renderDropdown(
              "risk-level",
              selectedRiskLevel,
              riskLevelOptions,
              onRiskLevelChange
            )}
            {renderDropdown(
              "age-group",
              selectedAgeGroup,
              ageGroupOptions,
              onAgeGroupChange
            )}
            {renderDropdown(
              "gender",
              selectedGender,
              genderOptions,
              onGenderChange
            )}
            {hasActiveFilters() && (
              <Button
                onClick={handleClearFilters}
                className="flex items-center space-x-2 !p-2 !px-3"
              >
                <X className="h-4 w-4" />
                <span className="text-sm">Clear Filters</span>
              </Button>
            )}
            <Link to="/add-patient" className={addPatientClass}>
              <AddPatientIcon className="h-4 w-4 shrink-0" />
              Add Patient
            </Link>
          </div>

          {/* Mobile Controls - Shown only on mobile */}
          <div className="flex w-full flex-col gap-2 md:hidden">
            <div className="flex w-full items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-guardian-80" />
                  <input
                    type="text"
                    placeholder="Search patients"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="h-10 w-full pl-10 pr-3 rounded-full bg-guardian-10 focus:outline-none text-sm font-normal text-gray-900 placeholder:text-gray-900"
                  />
                </div>
              </div>
              <Button
                onClick={() => setIsMobileFilterOpen(!isMobileFilterOpen)}
                className={`${pillButtonClass} !w-auto shrink-0 justify-between `}
              >
                <Filter className="h-5 w-5" />
                <span className="text-sm">Filters</span>
              </Button>
            </div>
            <Link to="/add-patient" className={addPatientClass}>
              <AddPatientIcon className="h-4 w-4 shrink-0" />
              Add Patient
            </Link>
          </div>

        </div>

        {/* Mobile Filter Dropdown */}
        {isMobileFilterOpen && (
          <div
            className="relative z-40 md:hidden border-t border-guardian-20 px-4 py-4 space-y-3"
            onClick={(e) => e.stopPropagation()}
          >
            {renderDropdown(
              "risk-level",
              selectedRiskLevel,
              riskLevelOptions,
              onRiskLevelChange
            )}
            {renderDropdown(
              "age-group",
              selectedAgeGroup,
              ageGroupOptions,
              onAgeGroupChange
            )}
            {renderDropdown(
              "gender",
              selectedGender,
              genderOptions,
              onGenderChange
            )}
            {hasActiveFilters() && (
              <Button
                onClick={handleClearFilters}
                className="flex items-center space-x-2 !p-2 !px-3 w-full !justify-center"
              >
                <X className="h-4 w-4" />
                <span className="text-sm">Clear Filters</span>
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
